import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

import { downgradePlan, resumeSubscription } from 'api/jalapeno';
import { ACCOUNT_QUERY_KEY } from 'api/queries/useAccountQuery';
import { SUBSCRIPTION_QUERY_KEY } from 'api/queries/useSubscriptionQuery';
import { ifLinks, mmLinks } from 'constants/links';
import Button from 'honeydew/Button';
import DowngradePlanIcon from 'honeydew/icons/MyPlan/DowngradePlanIcon';
import Select from 'honeydew/Select';
import TextField from 'honeydew/TextField';
import useGlobalStore from 'hooks/useGlobalStore';
import useTailwindTheme from 'hooks/useTailwindTheme';
import { trackEvent } from 'utils/analyticsUtils';

function CancelPlan({ account, closeCancelationModal, setError, setSuccess, subscription }) {
    const queryClient = useQueryClient();
    const { isMisfitsTheme } = useGlobalStore();
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });

    const [cancelReason, setCancelReason] = useState('');
    const [secondaryCancelReason, setSecondaryCancelReason] = useState('');
    const [cancelReasonOther, setCancelReasonOther] = useState('');

    const generateCancelReasonList = (reasons) =>
        reasons
            ?.map((reason) => ({
                value: reason,
                itemCopy: reason,
                name: reason,
                sort: reason === 'Other issues' || reason === 'Other' ? -1 : Math.random(),
            }))
            .sort((a, b) => b.sort - a.sort) || [];

    const cancelReasons = generateCancelReasonList(Object.keys(account?.downgradeReasons));
    const secondaryCancelReasons = generateCancelReasonList(account?.downgradeReasons[cancelReason]);

    const isDisabled =
        cancelReason === '' ||
        (secondaryCancelReason === '' && secondaryCancelReasons?.length > 0) ||
        (secondaryCancelReason === 'Other' && cancelReasonOther === '');

    const handleUpdateCancelReason = (e) => {
        setCancelReason(e.target.value);
        setSecondaryCancelReason('');
        setCancelReasonOther('');
    };

    const handleUpdateSecondaryCancelReason = (e) => {
        setSecondaryCancelReason(e.target.value);
        setCancelReasonOther('');
    };

    const handleResumeSubscription = async () => {
        try {
            await resumeSubscription(account.subID);
            queryClient.invalidateQueries({ queryKey: [ACCOUNT_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_QUERY_KEY] });
        } catch {
            setError('There was an error attempting to resume your subscription');
        }
    };

    const handleConfirmCancelClick = async () => {
        setError('');

        const sendParams = {
            reason: cancelReason,
            secondaryReason: secondaryCancelReason,
            details: cancelReasonOther,
        };

        if (subscription?.status === 'PAUSED') {
            handleResumeSubscription();
        }

        try {
            const response = await downgradePlan(subscription.id, sendParams);
            if (response.msg === 'Success') {
                queryClient.invalidateQueries({ queryKey: [ACCOUNT_QUERY_KEY] });
                queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_QUERY_KEY] });
                trackEvent('plan downgrade complete', { variant: 'membership' });
                setSuccess('downgrade');
                window.scrollTo({ top: 0, behavior: 'smooth' });
                closeCancelationModal();
            } else {
                setError('Unable cancel plan.');
            }
        } catch {
            setError('Unable cancel plan.');
        }
    };

    const needHelpClick = () => {
        trackEvent('cancel flow contact customer care');
    };

    return (
        <div className="text-center md:p-40">
            <div className="mx-auto mb-10 w-[75px]">
                <DowngradePlanIcon />
            </div>

            <h2 className="text-heading-sm mb-10">Cancel your subscription to shop without a commitment</h2>
            <p>
                If you cancel your subscription, you&apos;ll be downgraded to our{' '}
                <span className="font-grotesk-bold">Flex Plan</span> and only be charged if you choose to shop.
            </p>
            <div className="bg-malt-off-white mx-auto mt-20 rounded-md px-30 py-15 md:mt-40 md:w-[560px]">
                {!mobile ? (
                    <div className="flex gap-20 text-left">
                        <div>
                            <p className="mb-5 font-grotesk-bold">That means:</p>
                            <ul className="list-disc pl-20">
                                <li>No subscription (only shop when you need to!)</li>
                                <li>No pre-filled carts</li>
                                <li>No need to skip upcoming boxes</li>
                                <li>No checkout needed—simply meet your order minimum to place your order</li>
                            </ul>
                        </div>

                        <div>
                            <p className="mb-5 font-grotesk-bold">And you&apos;ll still:</p>
                            <ul className="list-disc pl-20">
                                <li>Receive shopping window reminder emails</li>
                                <li>Get access to exclusive deals and products</li>
                                <li>Earn loyalty points at 1x (instead of 2x)</li>
                            </ul>
                        </div>
                    </div>
                ) : (
                    <>
                        <p className="mb-5 font-grotesk-bold">That means:</p>
                        <ul className="list-inside list-disc pl-20">
                            <li>No subscription (only shop when you need to!)</li>
                            <li>No pre-filled carts</li>
                            <li>No need to skip upcoming boxes</li>
                            <li>No checkout needed—simply meet your order minimum to place your order</li>
                        </ul>
                        <br />
                        <p className="mb-5 font-grotesk-bold">And you&apos;ll still:</p>
                        <ul className="list-inside list-disc pl-20">
                            <li>Receive shopping window reminder emails</li>
                            <li>Get access to exclusive deals and products</li>
                            <li>Earn loyalty points at 1x (instead of 2x)</li>
                        </ul>
                    </>
                )}
            </div>

            <div className="mx-auto mt-30 text-left md:w-[380px]">
                <Select
                    labelId="cancel-reason-select"
                    id="cancelReasonSelect"
                    label="Tell us why"
                    value={cancelReason}
                    menuItems={cancelReasons}
                    onChange={handleUpdateCancelReason}
                    isMisfits={isMisfitsTheme}
                    required
                />

                {cancelReason !== '' && secondaryCancelReasons?.length > 0 && (
                    <div className="mt-20">
                        <Select
                            labelId="secondary-cancel-reason-select"
                            id="secondaryCancelReasonSelect"
                            label="Let’s get specific"
                            value={secondaryCancelReason}
                            menuItems={secondaryCancelReasons}
                            onChange={handleUpdateSecondaryCancelReason}
                            isMisfits={isMisfitsTheme}
                            required
                        />
                    </div>
                )}

                {secondaryCancelReason === 'Other' && (
                    <div className="mt-20">
                        <TextField
                            name="cancelReasonOther"
                            id="cancelReasonOther"
                            label="Please elaborate"
                            placeholder="Share your reason here"
                            value={cancelReasonOther}
                            onChange={(e) => setCancelReasonOther(e.target.value)}
                            rows={1}
                            multiline
                            required
                        />
                    </div>
                )}
            </div>

            <div>
                <div className="text-body-sm mx-auto mb-15 mt-25 md:w-[380px]">
                    {cancelReason !== '' && (
                        <Button size="md" onClick={handleConfirmCancelClick} disabled={isDisabled}>
                            Cancel Subscription
                        </Button>
                    )}
                </div>
                <a
                    href={isMisfitsTheme ? mmLinks.customerCare : ifLinks.customerCare}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link text-body-md"
                    onClick={needHelpClick}
                >
                    Need help? Contact us
                </a>
            </div>
            <p className="text-body-sm mt-20">
                Canceling will stop automatic future orders, but will not stop already confirmed orders.
            </p>
        </div>
    );
}

CancelPlan.propTypes = {
    account: PropTypes.shape({}),
    closeCancelationModal: PropTypes.func.isRequired,
    setError: PropTypes.func.isRequired,
    setSuccess: PropTypes.func.isRequired,
    subscription: PropTypes.shape({}),
};

CancelPlan.defaultProps = {
    account: {},
    subscription: {},
};

export default CancelPlan;
